import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/** Archive report: flag has participants array (per-participant) */
export interface ReportFlagParticipant {
  participant_id?: string;
  user_id?: string;
  full_name?: string;
  team?: string;
  obtained_score?: number;
  hint_used?: boolean;
  status?: boolean;
  updated_at?: string;
}

export interface ReportData {
  id: string;
  start_time: string;
  end_time?: string;
  total_obtained_score?: string | number;
  total_score?: string | number;
  /** Legacy single-user report */
  user?: {
    user_full_name?: string;
  };
  /** Archive report: started by user name */
  started_by?: string;
  /** Archive report: participants list (used to compute totals when no top-level total_obtained_score) */
  participants?: Array<{
    id?: string;
    user_id?: string;
    full_name?: string;
    team?: string;
    total_obtained_score?: number | string;
    total_score?: number | string;
  }>;
  scenario?: {
    id?: string;
    name?: string;
    description?: string;
    type?: string;
    category_name?: string;
    difficulty?: string;
    duration?: number;
    tags?: string[];
    points?: number;
    ttx_enabled?: boolean;
    severity?: string;
  };
  flags?: Array<{
    id: string;
    question?: string;
    hint?: string;
    score?: string | number;
    team?: string;
    type?: string | null;
    /** Legacy: single participant_answers */
    participant_answers?: {
      submitted_response?: string;
      obtained_score?: string | number;
      hint_used?: boolean;
      retries?: number;
      updated_at?: string;
      feedback?: string;
    };
    /** Archive: per-participant answers */
    participants?: ReportFlagParticipant[];
  }>;
  /** Optional: TTX-style participant rows (legacy shape; prefer immutable exercise report API for new data) */
  ttx?: Array<{
    id: number;
    user_id: number;
    user_email?: string;
    user_full_name?: string;
    role_id: number;
    role_name?: string;
    team?: string;
    total_obtained_score?: number;
    total_score?: number;
    injects?: Array<{
      id: number;
      inject_id: number;
      title?: string;
      points?: number;
      points_type?: string | null;
      sent_at?: string;
      response_text?: string;
      responded_at?: string | null;
      obtained_score?: number;
      bonus_points?: number;
      evaluated_at?: string | null;
      evaluated_by?: number | null;
      evaluator_name?: string;
      remarks?: string;
      is_evaluated?: boolean;
      status?: string;
    }>;
  }>;
  phase_scores?: Array<{
    phase_id: number;
    phase_name?: string;
    user_id: number;
    user_full_name?: string;
    obtained_score?: number;
    max_score?: number;
  }>;
  sent_inject_ids?: number[];
}

/**
 * Generate comprehensive CSV export with all report data
 */
export function generateComprehensiveCSV(report: ReportData): string {
  const csvSections: string[] = [];

  // Compute totals: support archive report (participants array) or legacy (top-level totals)
  const partList = report.participants ?? [];
  const obtainedFromParts = partList.reduce((s, p) => s + Number(p.total_obtained_score ?? 0), 0);
  const totalFromParts = partList.reduce((s, p) => s + Number(p.total_score ?? 0), 0);
  const obtained = Number(report.total_obtained_score ?? obtainedFromParts ?? 0);
  const total = Number(report.total_score ?? (totalFromParts || 1));
  const percentage = total > 0 ? ((obtained / total) * 100).toFixed(2) : '0';
  const reportUser = report.started_by ?? report.user?.user_full_name ?? 'N/A';

  // Run duration (from start_time / end_time)
  let durationStr = 'N/A';
  if (report.start_time && report.end_time) {
    const start = new Date(report.start_time).getTime();
    const end = new Date(report.end_time).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      const ms = end - start;
      const min = Math.floor(ms / 60000);
      const sec = Math.floor((ms % 60000) / 1000);
      durationStr = `${min} min ${sec} sec`;
    }
  }

  // Section 1: Report Summary
  csvSections.push('REPORT SUMMARY');
  csvSections.push('Field,Value');
  csvSections.push(`Report ID,"${report.id}"`);
  csvSections.push(`Started By,"${String(reportUser).replace(/"/g, '""')}"`);
  csvSections.push(`Start Time,"${report.start_time || 'N/A'}"`);
  csvSections.push(`End Time,"${report.end_time || 'In Progress'}"`);
  csvSections.push(`Run Duration,${durationStr}`);
  csvSections.push(`Total Score,"${obtained}/${total}"`);
  csvSections.push(`Completion Percentage,${percentage}%`);
  if (partList.length) {
    csvSections.push(`Participants,${partList.length}`);
  }
  csvSections.push('');

  // Section 2: Scenario Details
  csvSections.push('SCENARIO DETAILS');
  csvSections.push('Field,Value');
  csvSections.push(`Scenario ID,"${report.scenario?.id || 'N/A'}"`);
  csvSections.push(`Scenario Name,"${(report.scenario?.name || 'N/A').replace(/"/g, '""')}"`);
  csvSections.push(`Description,"${(report.scenario?.description || 'N/A').replace(/"/g, '""')}"`);
  csvSections.push(`Type,"${report.scenario?.type || 'N/A'}"`);
  csvSections.push(`Category,"${report.scenario?.category_name || 'N/A'}"`);
  csvSections.push(`Severity,"${report.scenario?.severity || 'N/A'}"`);
  csvSections.push(`Difficulty,"${report.scenario?.difficulty || 'N/A'}"`);
  csvSections.push(`Duration,"${report.scenario?.duration ? report.scenario.duration + ' min' : 'N/A'}"`);
  csvSections.push(`Max Points,"${report.scenario?.points ?? 'N/A'}"`);
  
  if (report.scenario?.tags && report.scenario.tags.length > 0) {
    csvSections.push(`Tags,"${report.scenario.tags.join(', ')}"`);
  }
  csvSections.push('');

  // Section 4: TTX Summary (optional)
  if (report.scenario?.ttx_enabled || (report.ttx && report.ttx.length > 0)) {
    const ttxParticipants = report.ttx || [];
    const phaseScores = report.phase_scores || [];
    const sentInjectIds = report.sent_inject_ids || [];

    csvSections.push('TTX SUMMARY');
    csvSections.push('Field,Value');
    csvSections.push(`TTX Enabled,${report.scenario?.ttx_enabled ? 'Yes' : 'No'}`);
    csvSections.push(`TTX Participants,${ttxParticipants.length}`);
    csvSections.push(`Phase Score Rows,${phaseScores.length}`);
    csvSections.push(`Sent Inject IDs,"${sentInjectIds.join(', ')}"`);
    csvSections.push('');

    // Section 5: TTX Participants
    csvSections.push('TTX PARTICIPANTS');
    csvSections.push('User ID,Name,Email,Role,Team,Score Obtained,Score Total,Inject Deliveries');
    for (const p of ttxParticipants) {
      const name = (p.user_full_name || '').replace(/"/g, '""');
      const email = (p.user_email || '').replace(/"/g, '""');
      const role = (p.role_name || String(p.role_id || '')).replace(/"/g, '""');
      const team = (p.team || '').replace(/"/g, '""');
      const obtained = Number(p.total_obtained_score || 0);
      const total = Number(p.total_score || 0);
      const injectCount = (p.injects || []).length;
      csvSections.push(
        `${p.user_id},"${name}","${email}","${role}","${team}",${obtained},${total},${injectCount}`,
      );
    }
    csvSections.push('');

    // Section 6: TTX Inject Deliveries (flattened)
    csvSections.push('TTX INJECT DELIVERIES');
    csvSections.push('User ID,Participant Assignment ID,Delivery ID,Inject ID,Title,Status,Points,Obtained,Bonus,Sent At,Responded At,Evaluator,Remarks');
    for (const p of ttxParticipants) {
      for (const d of p.injects || []) {
        const title = (d.title || '').replace(/"/g, '""');
        const remarks = (d.remarks || '').replace(/"/g, '""');
        const evaluator = (d.evaluator_name || '').replace(/"/g, '""');
        csvSections.push(
          `${p.user_id},${p.id},${d.id},${d.inject_id},"${title}","${d.status || ''}",${Number(d.points || 0)},${Number(d.obtained_score || 0)},${Number(d.bonus_points || 0)},"${d.sent_at || ''}","${d.responded_at || ''}","${evaluator}","${remarks}"`,
        );
      }
    }
    csvSections.push('');

    // Section 7: Phase Scores
    if (phaseScores.length) {
      csvSections.push('TTX PHASE SCORES');
      csvSections.push('Phase ID,Phase Name,User ID,User Name,Obtained,Max');
      for (const ps of phaseScores) {
        const phaseName = (ps.phase_name || '').replace(/"/g, '""');
        const userName = (ps.user_full_name || '').replace(/"/g, '""');
        csvSections.push(
          `${ps.phase_id},"${phaseName}",${ps.user_id},"${userName}",${Number(ps.obtained_score || 0)},${Number(ps.max_score || 0)}`,
        );
      }
      csvSections.push('');
    }
  }

  // Section 3: Participants (archive report)
  if (partList.length) {
    csvSections.push('PARTICIPANTS');
    csvSections.push('ID,User ID,Full Name,Team,Score Obtained,Score Total');
    for (const p of partList) {
      const name = (p.full_name ?? '').replace(/"/g, '""');
      const team = (p.team ?? '').replace(/"/g, '""');
      csvSections.push(
        `"${p.id ?? ''}","${p.user_id ?? ''}","${name}","${team}",${Number(p.total_obtained_score ?? 0)},${Number(p.total_score ?? 0)}`,
      );
    }
    csvSections.push('');
  }

  // Section 4: Performance Metrics (support archive flags with participants array)
  const flags = report.flags || [];
  let correctCount = 0;
  let hintUsedCount = 0;
  let totalAttempts = 0;
  for (const f of flags) {
    if (f.participants?.length) {
      for (const fp of f.participants) {
        totalAttempts += 1;
        if (Number(fp.obtained_score ?? 0) > 0 || fp.status) correctCount += 1;
        if (fp.hint_used) hintUsedCount += 1;
      }
    } else if (f.participant_answers) {
      totalAttempts += 1;
      if (Number(f.participant_answers?.obtained_score || 0) > 0) correctCount += 1;
      if (f.participant_answers?.hint_used) hintUsedCount += 1;
    }
  }
  if (totalAttempts === 0 && flags.length) {
    totalAttempts = flags.length;
    correctCount = flags.filter(f => Number(f.participant_answers?.obtained_score || 0) > 0).length;
    hintUsedCount = flags.filter(f => f.participant_answers?.hint_used).length;
  }
  const accuracy = totalAttempts > 0 ? ((correctCount / totalAttempts) * 100).toFixed(2) : '0';

  csvSections.push('PERFORMANCE METRICS');
  csvSections.push('Metric,Value');
  csvSections.push(`Total Flag Opportunities,${totalAttempts}`);
  csvSections.push(`Correct/Solved,${correctCount}`);
  csvSections.push(`Missed,${totalAttempts - correctCount}`);
  csvSections.push(`Accuracy,${accuracy}%`);
  csvSections.push(`Hints Used,${hintUsedCount}`);
  csvSections.push('');

  // Flag type breakdown (like phase breakdown for injects)
  const byType = new Map<string, { flagCount: number; opportunities: number; solved: number }>();
  for (const f of flags) {
    const typeLabel = (f.type ?? '').trim() || 'No type';
    let opportunities = 0;
    let solved = 0;
    if (f.participants?.length) {
      opportunities = f.participants.length;
      solved = f.participants.filter((fp) => Number(fp.obtained_score ?? 0) > 0 || fp.status).length;
    } else if (f.participant_answers) {
      opportunities = 1;
      solved = Number(f.participant_answers?.obtained_score || 0) > 0 ? 1 : 0;
    }
    const ex = byType.get(typeLabel) ?? { flagCount: 0, opportunities: 0, solved: 0 };
    ex.flagCount += 1;
    ex.opportunities += opportunities;
    ex.solved += solved;
    byType.set(typeLabel, ex);
  }
  if (byType.size) {
    csvSections.push('FLAG TYPE BREAKDOWN');
    csvSections.push('Type,Flags,Opportunities,Solved,Success %');
    for (const [typeLabel, row] of Array.from(byType.entries()).sort((a, b) => b[1].opportunities - a[1].opportunities)) {
      const pct = row.opportunities > 0 ? Math.round((row.solved / row.opportunities) * 100) : 0;
      csvSections.push(`"${typeLabel.replace(/"/g, '""')}",${row.flagCount},${row.opportunities},${row.solved},${pct}%`);
    }
    csvSections.push('');
  }

  csvSections.push('CHALLENGE DETAILS (Flags)');
  csvSections.push('Flag ID,Question,Team,Type,Max Score,Participant,Obtained,Status,Hint Used,Updated');
  for (const flag of flags) {
    const question = (flag.question || 'N/A').replace(/"/g, '""');
    const maxScore = Number(flag.score ?? 0);
    const typeVal = (flag.type ?? '').replace(/"/g, '""');
    if (flag.participants?.length) {
      for (const fp of flag.participants) {
        const obtained = Number(fp.obtained_score ?? 0);
        const status = obtained > 0 || fp.status ? 'Solved' : 'Not solved';
        const name = (fp.full_name ?? '').replace(/"/g, '""');
        csvSections.push(
          `"${flag.id}","${question}","${flag.team ?? ''}","${typeVal}",${maxScore},"${name}",${obtained},${status},${fp.hint_used ? 'Yes' : 'No'},"${fp.updated_at ?? ''}"`,
        );
      }
    } else {
      const obtained = Number(flag.participant_answers?.obtained_score || 0);
      const status = obtained > 0 ? 'Complete' : 'Incomplete';
      csvSections.push(
        `"${flag.id}","${question}","${flag.team ?? 'N/A'}","${typeVal}",${maxScore},"",${obtained},${status},${flag.participant_answers?.hint_used ? 'Yes' : 'No'},"${flag.participant_answers?.updated_at || 'N/A'}"`,
      );
    }
  }

  return csvSections.join('\n');
}

/**
 * Generate simple CSV export (flags only; supports archive format with participants array)
 */
export function generateSimpleCSV(flags: ReportData['flags'] = []): string {
  const headers = ['flag_id', 'question', 'participant', 'obtained_score', 'hint_used', 'status', 'score', 'team', 'updated_at'];
  const rows: string[][] = [];
  for (const f of flags ?? []) {
    if (f.participants?.length) {
      for (const fp of f.participants) {
        rows.push([
          f.id,
          `"${(f.question ?? '').replace(/"/g, '""')}"`,
          `"${(fp.full_name ?? '').replace(/"/g, '""')}"`,
          String(fp.obtained_score ?? ''),
          fp.hint_used ? 'Yes' : 'No',
          (Number(fp.obtained_score ?? 0) > 0 || fp.status) ? 'Solved' : 'Not solved',
          String(f.score ?? ''),
          f.team ?? '',
          fp.updated_at ?? '',
        ]);
      }
    } else {
      rows.push([
        f.id,
        `"${(f.question ?? '').replace(/"/g, '""')}"`,
        '',
        String(f.participant_answers?.obtained_score ?? ''),
        f.participant_answers?.hint_used ? 'Yes' : 'No',
        Number(f.participant_answers?.obtained_score ?? 0) > 0 ? 'Solved' : 'Not solved',
        String(f.score ?? ''),
        f.team ?? '',
        f.participant_answers?.updated_at ?? '',
      ]);
    }
  }
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Strip HTML tags for plain-text PDF content
 */
function stripHtml(html: string | undefined): string {
  if (html == null || html === '') return '';
  const doc = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (doc) {
    doc.innerHTML = html;
    return (doc.textContent || doc.innerText || '').trim();
  }
  return String(html).replace(/<[^>]*>/g, '').trim();
}

/**
 * Generate PDF from report element
 */
export async function generatePDF(
  elementId: string,
  filename: string,
  options: {
    onProgress?: (progress: number) => void;
    quality?: number;
  } = {}
): Promise<void> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Report content not found. Ensure the report is loaded and the page has finished rendering.`);
  }
  const hasContent = element.offsetHeight > 0 && element.offsetWidth > 0 && (element.children.length > 0 || (element.textContent || '').trim().length > 0);
  if (!hasContent) {
    throw new Error(`Report content is empty. Load the report and try again.`);
  }

  try {
    options.onProgress?.(10);

    // Capture SVG container dimensions from the visible page so we can apply them in the clone (html2canvas renders SVGs better with explicit dimensions)
    const svgDimensions: { w: number; h: number }[] = [];
    element.querySelectorAll('svg').forEach((svg) => {
      const parent = svg.parentElement as HTMLElement | null;
      const w = parent ? Math.max(1, parent.offsetWidth || parent.clientWidth || 800) : 800;
      const h = parent ? Math.max(1, parent.offsetHeight || parent.clientHeight || 200) : 200;
      svgDimensions.push({ w, h });
    });

    // Clone the element to modify for PDF
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.width = '1200px';
    clone.style.overflow = 'visible';
    clone.style.minHeight = `${element.offsetHeight}px`;
    clone.style.backgroundColor = '#ffffff';
    document.body.appendChild(clone);

    options.onProgress?.(30);

    // Apply captured dimensions to SVGs in the clone so PDF export renders charts correctly
    clone.querySelectorAll('svg').forEach((svg, i) => {
      const d = svgDimensions[i];
      if (d) {
        svg.setAttribute('width', String(d.w));
        svg.setAttribute('height', String(d.h));
      }
    });

    // Before generating canvas, try to inline images within the clone (better for html2canvas)
    try {
      const clonedElement = clone.querySelector(`#${elementId}`) as HTMLElement | null;
      if (clonedElement) {
        clonedElement.style.backgroundColor = '#ffffff';
        // Set crossOrigin on images to improve chances of successful fetching
        const imgs = Array.from(clonedElement.querySelectorAll('img')) as HTMLImageElement[];
        imgs.forEach(i => i.setAttribute('crossorigin', 'anonymous'));

        // Run previous onclone DOM tweaks (hide buttons, expand details, hide links)
        // Hide ALL buttons in PDF (including Export Report, Back buttons, etc.)
        const buttons = clonedElement.querySelectorAll('button');
        buttons.forEach(btn => { btn.style.display = 'none'; });

        // Hide action links (Back to Reports, Back to Scenarios, View Scenario)
        const links = clonedElement.querySelectorAll('a');
        links.forEach(link => {
          const linkText = link.textContent || '';
          if (link.querySelector('button') || 
              linkText.includes('Back to Reports') ||
              linkText.includes('Back to Scenarios') ||
              linkText.includes('Back to Archive') ||
              linkText.includes('Back to Players') ||
              linkText.includes('View Scenario') ||
              linkText.includes('Download') ||
              linkText.includes('Export')) {
            link.style.display = 'none';
          }
        });

        // Expand all collapsed details elements for complete PDF
        const details = clonedElement.querySelectorAll('details');
        details.forEach(detail => { detail.setAttribute('open', 'true'); });

        // Remove hover states and transitions
        clonedElement.style.transform = 'none';

        // Optional: Hide the entire button container div if it exists
        const buttonContainers = clonedElement.querySelectorAll('.flex.gap-2, .flex.gap-3');
        buttonContainers.forEach(container => {
          const hasOnlyButtons = Array.from(container.children).every(child => 
            child.tagName === 'BUTTON' || child.tagName === 'A' || (child.querySelector && child.querySelector('button'))
          );
          if (hasOnlyButtons && container.children.length > 0) {
            const el = container as HTMLElement;
            el.style.display = 'none';
          }
        });

        // Skip inlining images to avoid huge PDFs (base64 bloat). useCORS in html2canvas will try to load them.
        // If images fail due to CORS, they may appear blank but PDF stays small.
      }
    } catch (_err) {
      // if any preprocessing fails, continue to html2canvas anyway
    }

    // Allow clone to layout before capture (helps charts/SVGs render correctly in PDF)
    await new Promise((r) => requestAnimationFrame(r));

    // Generate canvas from HTML (scale 1 = smaller file; avoid scale 2 which can produce 400MB+ PDFs)
    const scale = Math.min(Math.max(options.quality ?? 1, 1), 2);
    const html2canvasOptions: Record<string, unknown> = {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 1200
    };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const canvas = await html2canvas(clone, html2canvasOptions as any);

    options.onProgress?.(70);

    // Remove clone
    document.body.removeChild(clone);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('PDF generation produced an empty image. Try refreshing the page and exporting again.');
    }

    // Use JPEG instead of PNG - typically 10-50x smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    const imgFormat = 'JPEG';

    // Create PDF
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    const pdf = new jsPDF('p', 'mm', 'a4');

    // Add first page
    pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    options.onProgress?.(90);

    // Save PDF
    pdf.save(filename);

    options.onProgress?.(100);
  } catch (error) {
    console.error('Error generating PDF:', error);
    const message = error instanceof Error ? error.message : 'Failed to generate PDF.';
    throw new Error(message);
  }
}

/**
 * Generate detailed PDF report with text content (alternative to visual PDF)
 */
export async function generateDetailedPDF(report: ReportData, filename: string): Promise<void> {
  if (!report || typeof report !== 'object') {
    throw new Error('Report data is missing. Cannot generate PDF.');
  }
  if (!report.id) {
    throw new Error('Report ID is missing. Invalid report data.');
  }

  const pdf = new jsPDF();
  let yPos = 20;
  const pageHeight = pdf.internal.pageSize.height;
  const margin = 20;
  const lineHeight = 7;

  // Helper to add new page if needed
  const checkPageBreak = () => {
    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = 20;
    }
  };

  // Title
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Performance Report', margin, yPos);
  yPos += lineHeight * 2;

  // Report Info (support archive: started_by and participants)
  const reportUser = report.started_by ?? report.user?.user_full_name ?? 'N/A';
  const partList = report.participants ?? [];
  const obtainedPdf = Number(report.total_obtained_score ?? partList.reduce((s, p) => s + Number(p.total_obtained_score ?? 0), 0));
  const totalPdf = Number(report.total_score ?? (partList.reduce((s, p) => s + Number(p.total_score ?? 0), 0) || 1));

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Started by: ${reportUser}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`Report ID: ${report.id}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`Start Time: ${report.start_time || 'N/A'}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`End Time: ${report.end_time || 'In Progress'}`, margin, yPos);
  yPos += lineHeight;
  if (report.start_time && report.end_time) {
    const start = new Date(report.start_time).getTime();
    const end = new Date(report.end_time).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      const ms = end - start;
      const min = Math.floor(ms / 60000);
      const sec = Math.floor((ms % 60000) / 1000);
      pdf.text(`Run Duration: ${min} min ${sec} sec`, margin, yPos);
      yPos += lineHeight;
    }
  }
  if (partList.length) {
    pdf.text(`Participants: ${partList.length}`, margin, yPos);
    yPos += lineHeight;
  }
  yPos += lineHeight;

  checkPageBreak();

  // Scenario Details
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Scenario Details', margin, yPos);
  yPos += lineHeight * 1.5;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Name: ${report.scenario?.name || 'N/A'}`, margin, yPos);
  yPos += lineHeight;

  // Wrap long description (strip HTML for plain text)
  const rawDescription = report.scenario?.description;
  if (rawDescription) {
    const plainDescription = stripHtml(typeof rawDescription === 'string' ? rawDescription : String(rawDescription));
    const splitDescription = pdf.splitTextToSize(plainDescription, 170);
    pdf.text(`Description: ${splitDescription[0] ?? ''}`, margin, yPos);
    yPos += lineHeight;
    for (let i = 1; i < splitDescription.length; i++) {
      checkPageBreak();
      pdf.text(splitDescription[i], margin + 20, yPos);
      yPos += lineHeight;
    }
  }

  pdf.text(`Type: ${report.scenario?.type || 'N/A'}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`Category: ${report.scenario?.category_name || 'N/A'}`, margin, yPos);
  yPos += lineHeight;
  if (report.scenario?.severity) {
    pdf.text(`Severity: ${report.scenario.severity}`, margin, yPos);
    yPos += lineHeight;
  }
  pdf.text(`Difficulty: ${report.scenario?.difficulty || 'N/A'}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`Duration: ${report.scenario?.duration ? report.scenario.duration + ' min' : 'N/A'}`, margin, yPos);
  yPos += lineHeight * 2;

  checkPageBreak();

  // Performance Summary (use computed totals for archive)
  const percentage = totalPdf > 0 ? ((obtainedPdf / totalPdf) * 100).toFixed(2) : '0';

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Performance Summary', margin, yPos);
  yPos += lineHeight * 1.5;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Score: ${obtainedPdf} / ${totalPdf} (${percentage}%)`, margin, yPos);
  yPos += lineHeight;

  const flags = report.flags || [];
  let correctCountPdf = 0;
  let hintUsedCountPdf = 0;
  let totalFlagAttempts = 0;
  for (const f of flags) {
    if (f.participants?.length) {
      for (const fp of f.participants) {
        totalFlagAttempts += 1;
        if (Number(fp.obtained_score ?? 0) > 0 || fp.status) correctCountPdf += 1;
        if (fp.hint_used) hintUsedCountPdf += 1;
      }
    } else {
      totalFlagAttempts += 1;
      if (Number(f.participant_answers?.obtained_score || 0) > 0) correctCountPdf += 1;
      if (f.participant_answers?.hint_used) hintUsedCountPdf += 1;
    }
  }

  pdf.text(`Flag success: ${correctCountPdf} / ${totalFlagAttempts}`, margin, yPos);
  yPos += lineHeight;
  pdf.text(`Hints Used: ${hintUsedCountPdf}`, margin, yPos);
  yPos += lineHeight * 2;

  checkPageBreak();

  // Flag type breakdown
  const byTypePdf = new Map<string, { flagCount: number; opportunities: number; solved: number }>();
  for (const f of flags) {
    const typeLabel = (f.type ?? '').trim() || 'No type';
    let opportunities = 0;
    let solved = 0;
    if (f.participants?.length) {
      opportunities = f.participants.length;
      solved = f.participants.filter((fp) => Number(fp.obtained_score ?? 0) > 0 || fp.status).length;
    } else if (f.participant_answers) {
      opportunities = 1;
      solved = Number(f.participant_answers?.obtained_score || 0) > 0 ? 1 : 0;
    }
    const ex = byTypePdf.get(typeLabel) ?? { flagCount: 0, opportunities: 0, solved: 0 };
    ex.flagCount += 1;
    ex.opportunities += opportunities;
    ex.solved += solved;
    byTypePdf.set(typeLabel, ex);
  }
  if (byTypePdf.size) {
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Flag Type Breakdown', margin, yPos);
    yPos += lineHeight * 1.5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    for (const [typeLabel, row] of Array.from(byTypePdf.entries()).sort((a, b) => b[1].opportunities - a[1].opportunities)) {
      checkPageBreak();
      const pct = row.opportunities > 0 ? Math.round((row.solved / row.opportunities) * 100) : 0;
      pdf.text(`${typeLabel}: ${row.flagCount} flags, ${row.solved}/${row.opportunities} solved (${pct}%)`, margin + 5, yPos);
      yPos += lineHeight;
    }
    yPos += lineHeight;
  }

  checkPageBreak();

  // Challenges
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Challenge Details', margin, yPos);
  yPos += lineHeight * 1.5;

  flags.forEach((flag, index) => {
    checkPageBreak();

    const maxScore = Number(flag.score || 0);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${index + 1}. ${flag.question || 'N/A'}`, margin, yPos);
    yPos += lineHeight;
    pdf.setFont('helvetica', 'normal');
    const typeStr = flag.type ? ` | Type: ${flag.type}` : '';
    pdf.text(`Team: ${flag.team || 'N/A'} | Max score: ${maxScore}${typeStr}`, margin + 5, yPos);
    yPos += lineHeight;

    if (flag.participants?.length) {
      for (const fp of flag.participants) {
        const obtained = Number(fp.obtained_score ?? 0);
        pdf.text(`  • ${fp.full_name || 'Participant'}: ${obtained}/${maxScore} - ${obtained > 0 || fp.status ? 'Solved' : 'Not solved'}${fp.hint_used ? ' (hint used)' : ''}`, margin + 5, yPos);
        yPos += lineHeight;
      }
    } else {
      const obtained = Number(flag.participant_answers?.obtained_score || 0);
      pdf.text(`Score: ${obtained} / ${maxScore}`, margin + 5, yPos);
      yPos += lineHeight;
      pdf.text(`Status: ${obtained > 0 ? 'Complete' : 'Incomplete'}`, margin + 5, yPos);
      yPos += lineHeight;
      pdf.text(`Hint Used: ${flag.participant_answers?.hint_used ? 'Yes' : 'No'}`, margin + 5, yPos);
      yPos += lineHeight;
      if (flag.participant_answers?.submitted_response) {
        const splitResponse = pdf.splitTextToSize(`Submission: ${flag.participant_answers.submitted_response}`, 165);
        splitResponse.forEach((line: string) => {
          checkPageBreak();
          pdf.text(line, margin + 5, yPos);
          yPos += lineHeight;
        });
      }
    }
    yPos += lineHeight * 0.5;
  });

  // TTX section (optional)
  if (report.scenario?.ttx_enabled || (report.ttx && report.ttx.length > 0)) {
    const ttxParticipants = report.ttx || [];
    const phaseScores = report.phase_scores || [];

    checkPageBreak();
    yPos += lineHeight;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TTX Report', margin, yPos);
    yPos += lineHeight * 1.5;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`TTX Enabled: ${report.scenario?.ttx_enabled ? 'Yes' : 'No'}`, margin, yPos);
    yPos += lineHeight;
    pdf.text(`TTX Participants: ${ttxParticipants.length}`, margin, yPos);
    yPos += lineHeight * 1.5;

    // Participants summary
    ttxParticipants.forEach((p, idx) => {
      checkPageBreak();
      const name = p.user_full_name || p.user_email || `User ${p.user_id}`;
      const role = p.role_name || String(p.role_id || '');
      const obtained = Number(p.total_obtained_score || 0);
      const total = Number(p.total_score || 0);
      const safeTotal = total > 0 ? total : (obtained > 0 ? obtained : 0);
      const injectCount = (p.injects || []).length;

      pdf.setFont('helvetica', 'bold');
      pdf.text(`${idx + 1}. ${name} (${role})`, margin, yPos);
      yPos += lineHeight;
      pdf.setFont('helvetica', 'normal');
      pdf.text(`User ID: ${p.user_id}`, margin + 5, yPos);
      yPos += lineHeight;
      pdf.text(`Score: ${obtained} / ${safeTotal}`, margin + 5, yPos);
      yPos += lineHeight;
      pdf.text(`Inject deliveries: ${injectCount}`, margin + 5, yPos);
      yPos += lineHeight;

      // Injects (compact)
      (p.injects || []).forEach((d) => {
        checkPageBreak();
        const title = d.title || `Inject ${d.inject_id}`;
        const got = Number(d.obtained_score || 0) + Number(d.bonus_points || 0);
        const pts = Number(d.points || 0);
        const status = d.status || '—';
        pdf.text(`- ${title} [${status}] score ${got}/${pts}`, margin + 8, yPos);
        yPos += lineHeight;
      });

      yPos += lineHeight * 0.5;
    });

    // Phase scores
    if (phaseScores.length) {
      checkPageBreak();
      yPos += lineHeight * 0.5;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Phase Scores', margin, yPos);
      yPos += lineHeight * 1.25;
      pdf.setFont('helvetica', 'normal');
      phaseScores.forEach((ps) => {
        checkPageBreak();
        const userName = ps.user_full_name || `User ${ps.user_id}`;
        pdf.text(
          `- ${ps.phase_name || ps.phase_id}: ${userName} ${Number(ps.obtained_score || 0)}/${Number(ps.max_score || 0)}`,
          margin + 5,
          yPos,
        );
        yPos += lineHeight;
      });
    }
  }

  pdf.save(filename);
}

